import { useCallback } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { TASK_FOLDER_DEFAULT_ID } from '../../constants'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import Log from '../../services/api/log'
import { useReorderTask } from '../../services/api/tasks.hooks'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { TIconColor } from '../../styles/colors'
import { pointerQuery } from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { DAILY_HOME_PATH } from '../../utils/dailyHome'
import { DropItem, DropType, TTaskFolder } from '../../utils/types'
import { countWithOverflow } from '../../utils/utils'
import { Icon, TIconType } from '../atoms/Icon'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import { useCalendarContext } from '../calendar/CalendarContext'
import Tip from '../radix/Tip'

export const CollapsedIconContainer = styled.div<{ isSelected?: boolean }>`
    padding: ${Spacing._8};
    margin: 0 ${Spacing._8};
    border-radius: ${Border.radius.medium};
    cursor: pointer;
    ${({ isSelected }) =>
        isSelected &&
        `
        background-color: ${Colors.background.white};
        border: ${Border.stroke.medium} solid ${Colors.background.border};
        color: ${Colors.text.title};
    `}
    /* This hover overrides the selected background, so a :hover left stuck on the link you just
       tapped would erase the "you are here" marker on exactly the folder you navigated to. */
    ${pointerQuery.canHover} {
        :hover {
            background-color: ${Colors.background.white};
        }
    }
`
/** Makes a CollapsedIconContainer clickable. The icon carries no text, so give it an aria-label. */
export const CollapsedNavButton = styled(NoStyleButton)`
    display: block;
    width: 100%;
`
const LinkContainer = styled.div<{ isSelected: boolean; isOver: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: ${Spacing._36};
    padding: 0 10px;
    margin: 0;
    flex: 1;
    min-width: 0;
    border-radius: ${Border.radius.medium};
    background-color: ${({ isOver, isSelected }) =>
        isOver ? Colors.background.white : isSelected ? Colors.background.white : 'transparent'};
    color: ${({ isSelected }) => (isSelected ? Colors.text.ink : Colors.text.base)};
    box-sizing: border-box;
    border: ${Border.stroke.medium} solid ${({ isSelected }) => (isSelected ? Colors.background.border : 'transparent')};
    gap: 10px;
    transform: translate(0, 0); // to hide corners when dragging
    /* Same as CollapsedIconContainer: hover outranks the selected background. */
    ${pointerQuery.canHover} {
        :hover {
            background-color: ${Colors.background.white};
            border-color: ${Colors.background.border};
        }
    }
`
const FolderTitle = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    user-select: none;
    ${Typography.label.large};
`
const FolderTitleItemCount = styled.span`
    margin-left: auto;
    user-select: none;
    ${Typography.body.small};
`
export const NavigationLinkTemplate = styled.div`
    width: 100%;
    /* 4px between nav items. Padding rather than margin: the links sit in several different parents,
       and margins would collapse in the block ones. */
    padding: 2px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
`

interface NavigationLinkProps {
    isCurrentPage: boolean
    link: string
    title: string
    icon?: TIconType
    iconColor?: TIconColor
    taskFolder?: TTaskFolder
    count?: number
    needsRelinking?: boolean
    draggable?: boolean
    droppable?: boolean
    isCollapsed?: boolean
}
const NavigationLink = ({
    isCurrentPage,
    link,
    title,
    icon,
    iconColor,
    taskFolder,
    count,
    needsRelinking = false,
    draggable = false,
    droppable,
    isCollapsed = false,
}: NavigationLinkProps) => {
    const { mutate: reorderTask } = useReorderTask()
    const { showTaskToCalSidebar, setShowTaskToCalSidebar, calendarType } = useCalendarContext()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const onDrop = useCallback(
        (item: DropItem) => {
            if (!taskFolder || !droppable || !item.task) return
            if (taskFolder.id === item.sectionId) return
            reorderTask(
                {
                    id: item.id,
                    orderingId: 1,
                    dropSectionId: taskFolder.id,
                    dragSectionId: item.sectionId,
                },
                item.task.optimisticId
            )
        },
        [taskFolder]
    )

    const isReadOnly = useIsReadOnly()
    const [, drag] = useDrag(
        () => ({
            type: DropType.FOLDER,
            item: { id: taskFolder?.id },
            canDrag: draggable && !isReadOnly,
            collect: (monitor) => monitor.isDragging(),
        }),
        [taskFolder, draggable, isReadOnly]
    )

    const [isOver, drop] = useDrop(
        () => ({
            accept: [DropType.TASK],
            collect: (monitor) => Boolean(taskFolder && droppable && monitor.isOver()),
            drop: onDrop,
            canDrop: () => !!(taskFolder && droppable),
        }),
        [taskFolder, onDrop]
    )

    const navigateToLink = () => {
        if (!showTaskToCalSidebar && calendarType === 'week') {
            setShowTaskToCalSidebar(true)
        }
        if (pathname === link && link !== DAILY_HOME_PATH) return
        Log(`navigate__${link}`)
        navigate(link, { replace: pathname === link })
    }
    const onClickHandler = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
        if (taskFolder?.id === TASK_FOLDER_DEFAULT_ID) e.preventDefault()
        navigateToLink()
    }

    if (isCollapsed && icon) {
        return (
            <CollapsedNavButton type="button" aria-label={title} data-navigation-link={link} onClick={onClickHandler}>
                <CollapsedIconContainer isSelected={isCurrentPage}>
                    <Icon icon={icon} size="default" color={iconColor} />
                </CollapsedIconContainer>
            </CollapsedNavButton>
        )
    }
    return (
        <NavigationLinkTemplate ref={drop} data-navigation-link={link} onClick={onClickHandler}>
            <LinkContainer ref={drag} isSelected={isCurrentPage} isOver={isOver}>
                {icon && <Icon icon={icon} color={iconColor} />}
                <FolderTitle>{title}</FolderTitle>
                {needsRelinking && (
                    <Tip content="Account needs to be re-linked in settings">
                        <Icon icon={icons.warning} color="red" />
                    </Tip>
                )}
                <FolderTitleItemCount>{count && countWithOverflow(count)}</FolderTitleItemCount>
            </LinkContainer>
        </NavigationLinkTemplate>
    )
}

export default NavigationLink
